import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const AutomotiveSolutions = () => {
  const phrases = [
    "ASIL-D ADAS Integration",
    "Connected V2X Telemetry Sync",
    "EV Powertrain Thermal Modeling"
  ];

  const cardData = [
    { type: 'auto-core', content: '🚗' },
    { type: 'safety-red', content: '🏎️' },
    { type: 'telemetry-stack', content: '📊' },
    { type: 'powertrain-spark', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🚗", title: "Vehicle Control Unit (VCU) Firmware Setup", desc: "Integrate low-level electronic control units directly with vehicle CAN-bus networks to track sensor output, throttle response, and dynamic chassis configurations instantly." },
    { icon: "⚡", title: "Connected V2X Telemetry Architecture", desc: "Connect modern vehicle test fleets securely using V2X, 5G, and MQTT protocols to capture microsecond speed, GPS positioning, and battery cell diagnostics." },
    { icon: "⚙️", title: "AI Predictive Fleet Diagnostics", desc: "Deploy automated machine learning pipelines to detect anomalous drivetrain vibration trends, forecast brake rotor decay, and prevent costly road-test failures." },
    { icon: "📊", title: "EV Range & Battery Optimization Lenses", desc: "Ingest and process cell-level battery logs into clean executive metrics tracking pack-level state of health, discharge curves, and thermal margins." },
    { icon: "🔗", title: "Just-In-Time Assembly Line Synchronization", desc: "Unify supplier component schedules, automated stamping plants, engine-to-chassis marrying, and structural material requirement planning frameworks." },
    { icon: "🛡️", title: "ISO 21434 Cybersecurity & Zero-Trust Architecture", desc: "Isolate vehicle infotainment networks from critical safety-critical control units using hardware security modules, secure gateways, and air-gapped data brokers." },
    { icon: "🔄", title: "Automated Hardware-in-the-Loop (HIL) Testing", desc: "Automate dynamic ecu test rigs, simulation loop exceptions, and routine sensor cluster recalibration matrices based on test track run hours." }
  ];

  const advancedServices = [
    { icon: "🔍", title: "Computer Vision Autonomous Pathing Calibration", desc: "Deploy high-speed vehicular camera and LiDAR networks running convolutional neural patterns to identify physical obstacles and lanes right during live road-testing." },
    { icon: "💼", title: "Chassis & Powertrain Digital Twin Modeling", desc: "Construct functional virtual mirrors of dynamic vehicle architectures to simulate aerodynamic changes and suspension loads prior to fabricating wind-tunnel prototypes." },
    { icon: "🗃️", title: "Thermal Management & EV Pack Efficiency Tuning", desc: "Track heat spikes across high-voltage lithium battery packs to optimize coolant pumping speeds and maximize vehicle driving range margins." },
    { icon: "⚖️", title: "Automated ISO 26262 Compliance & Track-and-Trace", desc: "Architect testing serialization frameworks recording immutable safety audit trails for safety-critical microchips, ensuring compliance with strict road safety rules." },
    { icon: "🚨", title: "Automated Assembly Robots & AGV Orchestration", desc: "Interface modern robotic welding lines and smart chassis-carrying automated guided vehicles directly into the primary body shop staging bays." },
    { icon: "📝", title: "PLM Design-to-CAD Assembly Integrations", desc: "Bridge CATIA and Siemens NX CAD engineering configurations directly into active manufacturing plant networks to eliminate physical build variation lag." },
    { icon: "🌐", title: "Edge Compute Gateway In-Car Implementations", desc: "Deploy highly ruggedized compute boxes directly on autonomous prototype test fleets to clean, compress, and analyze driving logs locally before cloud uploads." },
    { icon: "🧪", title: "Advanced ECU Calibration & Recipe Management", desc: "Implement strict, firmware-driven timing maps with zero-fault tolerances across heavy internal combustion, hybrid, or pure-electric vehicle platforms." },
    { icon: "📦", title: "Dynamic Fleet Demand & Global Parts Forecasting", desc: "Synthesize field vehicle telemetry markers with real-time dealership inventory data to adjust active assembly plant volume schedules dynamically." }
  ];

  const technologies = {
    "Vehicular Systems (CAN/ECU)": ["Vector CANalyzer / CANoe", "dSPACE HIL Testing Environments", "AUTOSAR Classic & Adaptive", "ETAS INCA ECU Calibration Tools"],
    "Telemetry & Cloud Analytics": ["AWS IoT FleetWise Systems", "Azure Connected Vehicle Platform", "Time-Series Databases (InfluxDB)", "Grafana Vehicle Performance Dashboards"],
    "Enterprise Automotive": ["SAP Automotive Supply Nodes", "Windchill PLM Document Sync", "Salesforce Automotive Cloud", "Custom CAD to Factory Pipelines"],
    "Autonomous & AI Systems": ["NVIDIA DRIVE Orin Platform", "ROS 2 Autonomous Driving Frameworks", "TensorFlow ADAS Object Detectors", "Ruggedized On-Vehicle Edge Gateways"],
    "Operational Compliance": ["ASIL-D Functional Safety Standards", "ISO 26262 Safety Lifecycle Gates", "ISO 21434 Cybersecurity Protocols", "IATF 16949 Automotive Quality Control"]
  };

  const processSteps = [
    { num: "01", title: "Vehicle Network & Architecture Auditing", desc: "Mapping automotive bus interfaces, identifying legacy sub-networks, and assessing historical message transmission lag." },
    { num: "02", title: "CAN-FD & Ethernet Topology Blueprinting", desc: "Structuring secure network partitions, specifying ECU message tag databases, and mapping signal-to-service payload structures." },
    { num: "03", title: "HIL Ingestion & Integration Synthesis", desc: "Configuring hardware-in-the-loop simulation interfaces, building diagnostic translation databases, and writing dynamic test-case routing logic." },
    { num: "04", title: "PLM & Factory System Mapping", desc: "Linking physical vehicle assembly lines directly to configuration management, spare parts logs, and supplier networks." },
    { num: "05", title: "Safety Validation & Cybersecurity Testing", desc: "Verifying functional safety response times, testing localized failover limits, and validating intrusion detection signatures." },
    { num: "06", title: "Track Go-Live & Fleet Sync", desc: "Activating real-time vehicle telemetry systems across active test fleets with zero interruption to current proving-ground run times." },
    { num: "07", title: "Continuous Calibration Loops", desc: "Reviewing fleet fuel and thermal efficiency profiles, reducing system payload bandwidth bottlenecks, and updating diagnostic algorithms regularly." }
  ];

  const industries = [
    "High-Volume Passenger Car & SUV Assembly Lines", "Cross-Border Specialized Commercial Truck & Bus Production", "Motorsports, Performance Racing & Prototyping",
    "High-Density Electric Vehicle (EV) Startup Facilities", "Specialized Autonomous Vehicle (AV) Development Fleets", "Tier-1 Powertrain, Chassis & Axle Assembly Plants",
    "High-Scale Semiconductor & Automotive Sensor Fabrication", "Off-Road Agricultural & Heavy Machinery Manufacturing", "Automotive Infotainment, Telematics & Software Labs",
    "Automotive Battery Cell, Module & Pack Giga-Factories", "Contract Vehicular Design & Engineering Operations", "Government Vehicle Safety Certification Laboratories"
  ];

  const designBenefits = [
    "Zero Calibration Discrepancies Across Vehicle Variants", "Up to 35% Reduction in Unplanned Proving-Ground Downtime", "100% ISO 21434 & UNECE R155 Compliant Architecture",
    "Guaranteed 99.95% Fleet Telemetry Network Ingest Availability", "Drastic Reduction in Manual Prototype Run Logging Tasks", "Highly Scalable High-Frequency Sensor Stream Ingestion",
    "Seamless Workshop-to-Boardroom Enterprise ERP Integration", "Real-Time Fleet Health & Range Management Metrics"
  ];

  const whyChooseUs = [
    "Certified Automotive Systems Architects & Functional Safety Engineers", "Proven Track Record Modernizing Tier-1 OEM Assembly & Proving Grounds", "Airtight ASIL-D Functional Safety & Cyber-Physical Security",
    "Deep Competency in High-Throughput In-Vehicle Time-Series Processing", "Platform-Agnostic ECU, CAN-bus, and Automotive Ethernet Interfacing", "Resilient High-Availability On-Board Edge Compute Implementations",
    "End-to-End Vehicle Development Lifecycle Validation & HIL Testing", "Transparent Testing Milestones & Verifiable Proving-Ground Metrics", "High-Availability Integration Infrastructure SLA Guarantees",
    "Experienced Automation Engineers Specializing in IATF 16949 Standards", "Iterative Proof-of-Concept Target Environment Test-Track Sprints", "100% Vehicle System Operations Stability and Functional Safety Assurances"
  ];

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          Automotive Solutions | Automotive ERP & Digital Transformation | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Automotive Solutions | Automotive ERP & Digital Transformation | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides Automotive Solutions, including automotive ERP, supply chain management, inventory management, dealer management systems, production planning, fleet management, quality control, and digital transformation services for the automotive industry."
        />

        <meta
          name="keywords"
          content="Automotive Solutions, Automotive ERP, Automotive Software, Dealer Management System, DMS Software, Fleet Management, Automotive Supply Chain, Inventory Management, Production Planning, Automotive Manufacturing, Vehicle Management Software, Automotive Analytics, Digital Transformation, Smart Automotive Solutions, Automotive Industry Software, Enterprise Automotive Solutions, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/automotive-solutions"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Automotive Solutions | Automotive ERP & Industry Software | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Optimize automotive operations with Affsoft's Automotive Solutions. We provide automotive ERP, dealer management systems, supply chain management, production planning, fleet management, inventory control, and digital transformation services."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/automotive-solutions"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Automotive Solutions | Automotive ERP & Digital Transformation | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Drive business growth with Affsoft Automotive Solutions. Our services include automotive ERP, dealer management systems, supply chain optimization, inventory management, fleet management, and smart automotive technologies."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="automotive-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0f111a' // Deep Metallic Slate fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Marquee Text */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Automotive &nbsp; Engineering &nbsp; Connected-Vehicle &nbsp; &amp; &nbsp;</span>
              <span>Autonomous Enterprise Architectures </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-red fs-3" style={{ color: '#dc2626' }}>✳</div>
                <h1 className="fw-bold mb-3 display-5">Automotive Systems</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we eliminate the vehicular data siloes and technical lag that cause test-fleet downtime. Our automotive engineering practice pairs modern vehicle network integration with robust functional safety software design and real-time fleet telemetry to convert physical vehicle builds into high-efficiency strategic assets.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Propose an Automotive Software Plan</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor fs-4 animate-blink" style={{ color: '#dc2626' }}>|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#dc2626' }}>Connected Vehicle Optimization</span>
              <h2 className="fw-bold text-dark display-6">Deploy High-Throughput Vehicle Networks & Fully Integrated HIL Laboratories</h2>
              <p className="text-muted mt-3">
                Isolated electronic controls, manual test-track logging, and reactive calibration setups impact automotive time-to-market. At Affsoft IT Solutions Pvt. Ltd., we build secure, highly reliable, and safety-validated connected vehicle architectures.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#dc2626' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Core Operational Gains of Strategic Enterprise Automotive Industry Transformation</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#dc2626' }}>Distributed Fleet Nodes</span>
              <h2 className="fw-bold text-dark display-6">Adaptive Autonomous Capabilities Built for High-Performance Testing</h2>
              <p className="text-muted mt-3">
                We scale basic vehicle logging systems into fully integrated, adaptive digital prototyping environments. From deploying computer vision perception checkpoints to managing battery pack thermal cycles, our blueprints protect your engineering program stability.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#dc2626' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#dc2626' }}>Vehicle Integration Matrix</span>
              <h2 className="fw-bold display-6">Automotive Platforms, Protocols & Connectivity Standards We Support</h2>
              <p className="text-light-50">We configure, monitor, and safeguard distributed automotive software environments utilizing strict functional safety standards.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#dc2626' }}>{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="opacity-75" style={{ color: '#dc2626' }}>•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Deployment Lifecycle</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Automotive Integration Blueprint</h2>
              <p className="text-muted">A structured, strictly governed lifecycle designed to safely transition vehicle software architectures from virtual models onto active physical proving grounds.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#dc2626' }}>
                    <div className="fw-bold display-6 mb-2" style={{ color: '#dc2626' }}>{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Automotive &amp; Mobility Fields We Transform</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏎️ {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine core functional safety validation with modern cloud engineering to guarantee highly predictable, certified automotive behavior.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="fs-4" style={{ color: '#dc2626' }}>🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default AutomotiveSolutions;